import firebase from "firebase/compat/app";
import { initDb, getZooId } from "../tools/conf";

/**
 * Data connector for personal activities (collection `activities`).
 *
 * An activity is a private, self-assigned reminder with its own due date,
 * optionally anchored to an entity (note, animal, enclosure, prescription...).
 * Unlike `tasks`, activities belong to a single user and are never visible
 * to anyone else: the collection is excluded from the default catch-all rule
 * and guarded by `userId == request.auth.uid` in firestore.rules.
 *
 * Reminder push is handled server-side by the `sendActivityReminders`
 * scheduled function, driven by the `reminderDueAtUnix` field (a single-field
 * range query, so no composite index is required): set to `dueAtUnix` while a
 * reminder is pending, and null once sent or once the activity is done.
 */
export class ActivityDatabaseConnector {
  /**
   * Resolve the current user id from localStorage (robust to the various
   * historical shapes), mirroring NotificationDatabaseConnector.
   * @returns {string|null}
   */
  _getCurrentUserId() {
    let userId = null;
    const userDataString = localStorage.getItem("userData");
    if (userDataString) {
      try {
        const userData = JSON.parse(userDataString);
        userId = userData.userId || userData.uid || userData.id;
      } catch (e) {
        console.error("[Activities] Error parsing userData:", e);
      }
    }
    if (!userId) {
      userId = localStorage.getItem("userId") || localStorage.getItem("uid");
    }
    return userId || null;
  }

  /**
   * Real-time listener on the current user's activities.
   * Equality-only query (userId + zooId) to avoid any composite index;
   * sorting is done in memory (open by dueAt asc, then done by doneAt desc).
   * Resolves to an unsubscribe function (always callable).
   * @param {(activities: Array<Object>) => void} onUpdate
   * @returns {Promise<() => void>}
   */
  async subscribeMyActivities(onUpdate) {
    try {
      const db = initDb();
      const zooId = await getZooId();
      const userId = this._getCurrentUserId();

      if (!userId) {
        console.error("[Activities] No user ID found for listener");
        return () => {};
      }

      const query = db
        .collection("activities")
        .where("userId", "==", userId)
        .where("zooId", "==", zooId);

      return query.onSnapshot(
        (snapshot) => {
          const activities = [];
          snapshot.forEach((doc) => {
            activities.push({ id: doc.id, activityId: doc.id, ...doc.data() });
          });
          activities.sort((a, b) => {
            if (a.status !== b.status) return a.status === "open" ? -1 : 1;
            if (a.status === "open") {
              return (a.dueAtUnix || 0) - (b.dueAtUnix || 0);
            }
            return (b.doneAtUnix || 0) - (a.doneAtUnix || 0);
          });
          onUpdate(activities);
        },
        (error) => {
          console.error("[Activities] Listener error:", error);
        }
      );
    } catch (error) {
      console.error("[Activities] Error setting up listener:", error);
      return () => {};
    }
  }

  /**
   * Real-time listener on ALL the current user's activities, across every zoo.
   * Reserved for Akongo staff (who hop between zoos): the query drops the
   * `zooId` filter, so each returned activity keeps its own `zooId` for the UI
   * to group by. Still an equality-only query (userId), so no composite index;
   * sorting mirrors `subscribeMyActivities`.
   * @param {(activities: Array<Object>) => void} onUpdate
   * @returns {Promise<() => void>}
   */
  async subscribeAllMyActivities(onUpdate) {
    try {
      const db = initDb();
      const userId = this._getCurrentUserId();

      if (!userId) {
        console.error("[Activities] No user ID found for cross-zoo listener");
        return () => {};
      }

      const query = db.collection("activities").where("userId", "==", userId);

      return query.onSnapshot(
        (snapshot) => {
          const activities = [];
          snapshot.forEach((doc) => {
            activities.push({ id: doc.id, activityId: doc.id, ...doc.data() });
          });
          activities.sort((a, b) => {
            if (a.status !== b.status) return a.status === "open" ? -1 : 1;
            if (a.status === "open") {
              return (a.dueAtUnix || 0) - (b.dueAtUnix || 0);
            }
            return (b.doneAtUnix || 0) - (a.doneAtUnix || 0);
          });
          onUpdate(activities);
        },
        (error) => {
          console.error("[Activities] Cross-zoo listener error:", error);
        }
      );
    } catch (error) {
      console.error("[Activities] Error setting up cross-zoo listener:", error);
      return () => {};
    }
  }

  /**
   * Create a personal activity for the current user.
   * @param {{title: string, note?: string, dueAt: Date,
   *          linkedEntity?: {type: string, id: string, name?: string, actionUrl?: string}|null,
   *          sourceRef?: {type: string, id: string}|null}} payload
   * @returns {Promise<{success: boolean, activityId?: string}>}
   */
  async _createActivity({
    title,
    note = "",
    dueAt,
    activityType = "todo",
    linkedEntity = null,
    sourceRef = null,
  }) {
    try {
      const db = initDb();
      const zooId = await getZooId();
      const userId = this._getCurrentUserId();
      if (!userId) throw new Error("No user ID found");
      if (!title || !dueAt) throw new Error("title and dueAt are required");

      const dueAtUnix = Math.floor(new Date(dueAt).getTime() / 1000);
      const nowUnix = Math.floor(Date.now() / 1000);
      const ref = db.collection("activities").doc();

      await ref.set({
        activityId: ref.id,
        userId,
        zooId,
        title: title.trim(),
        note: (note || "").trim(),
        activityType: activityType || "todo",
        dueAt: new Date(dueAt),
        dueAtUnix,
        linkedEntity: linkedEntity || null,
        sourceRef: sourceRef || null,
        status: "open",
        doneAt: null,
        doneAtUnix: null,
        reminderDueAtUnix: dueAtUnix,
        reminderSentAt: null,
        webNotifiedAt: null,
        createdAt: firebase.firestore.FieldValue.serverTimestamp(),
        createdAtUnix: nowUnix,
        updatedAt: firebase.firestore.FieldValue.serverTimestamp(),
        updatedAtUnix: nowUnix,
      });

      return { success: true, activityId: ref.id };
    } catch (error) {
      console.error("[Activities] Error creating activity:", error);
      return { success: false };
    }
  }

  /**
   * Update title / note / due date / linked entity of an activity.
   * Rescheduling re-arms the push reminder (reminderDueAtUnix follows the
   * new due date). Pass linkedEntity: null to detach the entity; leave it
   * undefined to keep it unchanged.
   * @param {string} activityId
   * @param {{title?: string, note?: string, dueAt?: Date, linkedEntity?: Object|null}} changes
   * @returns {Promise<{success: boolean}>}
   */
  async _updateActivity(activityId, changes) {
    try {
      const db = initDb();
      const nowUnix = Math.floor(Date.now() / 1000);
      const update = {
        updatedAt: firebase.firestore.FieldValue.serverTimestamp(),
        updatedAtUnix: nowUnix,
      };
      if (changes.title !== undefined) update.title = changes.title.trim();
      if (changes.note !== undefined) update.note = (changes.note || "").trim();
      if (changes.activityType !== undefined) {
        update.activityType = changes.activityType || "todo";
      }
      if (changes.linkedEntity !== undefined) {
        update.linkedEntity = changes.linkedEntity || null;
      }
      if (changes.dueAt) {
        const dueAtUnix = Math.floor(new Date(changes.dueAt).getTime() / 1000);
        update.dueAt = new Date(changes.dueAt);
        update.dueAtUnix = dueAtUnix;
        update.reminderDueAtUnix = dueAtUnix;
        update.reminderSentAt = null;
        // Re-arm the desktop alert too (browser notification / toast)
        update.webNotifiedAt = null;
        update.webDismissedAt = null;
      }
      await db.collection("activities").doc(activityId).update(update);
      return { success: true };
    } catch (error) {
      console.error("[Activities] Error updating activity:", error);
      return { success: false };
    }
  }

  /**
   * Record that the desktop (in-app/browser) alert was displayed. Written
   * once: a second display (reload, other tab) keeps the first date.
   * `webDismissedAt: null` marks the card as still to handle — it comes
   * back after a reload until it is dismissed, completed or rescheduled.
   * @param {string} activityId
   * @returns {Promise<{success: boolean, alreadyNotified?: boolean}>}
   */
  async _markActivityWebNotified(activityId) {
    try {
      const db = initDb();
      const ref = db.collection("activities").doc(activityId);
      // Client date on purpose (NOT serverTimestamp): a pending server
      // timestamp reads as null in latency-compensated snapshots, which
      // re-armed the alert guard and made the reminder card loop
      const alreadyNotified = await db.runTransaction(async (transaction) => {
        const snapshot = await transaction.get(ref);
        if (!snapshot.exists || snapshot.get("webNotifiedAt")) return true;
        transaction.update(ref, {
          webNotifiedAt: new Date(),
          webNotifiedAtUnix: Math.floor(Date.now() / 1000),
          webDismissedAt: null,
        });
        return false;
      });
      return { success: true, alreadyNotified };
    } catch (error) {
      console.error("[Activities] Error marking web-notified:", error);
      return { success: false };
    }
  }

  /**
   * Dismiss the desktop alert card: other tabs and reloads stop showing it.
   * Keeps the display date; fills it only if the display write was lost.
   * @param {string} activityId
   * @returns {Promise<{success: boolean}>}
   */
  async _dismissActivityWebAlert(activityId) {
    try {
      const db = initDb();
      const ref = db.collection("activities").doc(activityId);
      const nowUnix = Math.floor(Date.now() / 1000);
      await db.runTransaction(async (transaction) => {
        const snapshot = await transaction.get(ref);
        if (!snapshot.exists) return;
        const update = {
          webDismissedAt: new Date(),
          webDismissedAtUnix: nowUnix,
        };
        if (!snapshot.get("webNotifiedAt")) {
          update.webNotifiedAt = new Date();
          update.webNotifiedAtUnix = nowUnix;
        }
        transaction.update(ref, update);
      });
      return { success: true };
    } catch (error) {
      console.error("[Activities] Error dismissing web alert:", error);
      return { success: false };
    }
  }

  /**
   * A note notification was converted into a team task: flag the current
   * user's reminders created from it, so the bell stops offering the
   * conversion. Equality-only query (userId + sourceRef.id): no index.
   * @param {string} notificationId
   * @returns {Promise<{success: boolean, count: number}>}
   */
  async _markActivitiesConvertedFromNotification(notificationId) {
    try {
      const userId = this._getCurrentUserId();
      if (!userId || !notificationId) return { success: true, count: 0 };
      const db = initDb();
      const snapshot = await db
        .collection("activities")
        .where("userId", "==", userId)
        .where("sourceRef.id", "==", notificationId)
        .get();
      const refs = [];
      snapshot.forEach((doc) => {
        const data = doc.data() || {};
        if (data.sourceRef && data.sourceRef.type === "noteNotification" && !data.convertedToTaskAt) {
          refs.push(doc.ref);
        }
      });
      if (refs.length === 0) return { success: true, count: 0 };
      const batch = db.batch();
      const nowUnix = Math.floor(Date.now() / 1000);
      refs.forEach((ref) =>
        batch.update(ref, {
          convertedToTaskAt: new Date(),
          convertedToTaskAtUnix: nowUnix,
        })
      );
      await batch.commit();
      return { success: true, count: refs.length };
    } catch (error) {
      console.error("[Activities] Error flagging task conversion:", error);
      return { success: false, count: 0 };
    }
  }

  /**
   * Mark an activity as done (and disarm its reminder).
   * @param {string} activityId
   * @returns {Promise<{success: boolean}>}
   */
  async _completeActivity(activityId) {
    try {
      const db = initDb();
      const nowUnix = Math.floor(Date.now() / 1000);
      await db.collection("activities").doc(activityId).update({
        status: "done",
        doneAt: firebase.firestore.FieldValue.serverTimestamp(),
        doneAtUnix: nowUnix,
        reminderDueAtUnix: null,
        updatedAt: firebase.firestore.FieldValue.serverTimestamp(),
        updatedAtUnix: nowUnix,
      });
      return { success: true };
    } catch (error) {
      console.error("[Activities] Error completing activity:", error);
      return { success: false };
    }
  }

  /**
   * Reopen a done activity. The reminder is re-armed only if the due date is
   * still in the future (avoids an immediate push for an overdue item).
   * @param {string} activityId
   * @param {number|null} dueAtUnix
   * @returns {Promise<{success: boolean}>}
   */
  async _reopenActivity(activityId, dueAtUnix = null) {
    try {
      const db = initDb();
      const nowUnix = Math.floor(Date.now() / 1000);
      await db.collection("activities").doc(activityId).update({
        status: "open",
        doneAt: null,
        doneAtUnix: null,
        reminderDueAtUnix: dueAtUnix && dueAtUnix > nowUnix ? dueAtUnix : null,
        updatedAt: firebase.firestore.FieldValue.serverTimestamp(),
        updatedAtUnix: nowUnix,
      });
      return { success: true };
    } catch (error) {
      console.error("[Activities] Error reopening activity:", error);
      return { success: false };
    }
  }

  /**
   * Permanently delete an activity.
   * @param {string} activityId
   * @returns {Promise<{success: boolean}>}
   */
  async _deleteActivity(activityId) {
    try {
      const db = initDb();
      await db.collection("activities").doc(activityId).delete();
      return { success: true };
    } catch (error) {
      console.error("[Activities] Error deleting activity:", error);
      return { success: false };
    }
  }
}

export default ActivityDatabaseConnector;
